// Круглая кнопка игрового интерфейса.
//
// Вид кнопки — это состояние с явной таблицей переходов, а не набор флагов: кадр меняется
// на переходе, поэтому вид всегда отвечает тому, что игрок сделал мышью.
//
// Кнопка нарисована в квадратном кадре с прозрачными углами, поэтому область нажатия задана
// кругом. Иначе клик по углу кадра проходил бы мимо кнопки, но считался нажатием.
//
// Кнопка не прячется и не отключается от событий за всю свою жизнь. Только так Pixi продолжает
// следить за курсором: он проверяет попадание курсора в кнопку на каждом движении мыши, и если
// кнопку убрать из этой проверки, её вид после подмены кадров пришлось бы угадывать. Отдельно
// спросить, где курсор, нельзя: браузер не говорит его положение, пока мышь не двинулась.
//
// Заблокированная кнопка тускнеет. Своего кадра на этот случай художник дал не всем, а без
// разницы в виде блокировка не читается: игрок жмёт и не понимает, дошло нажатие или нет.
// Кнопке с таким кадром прозрачность не трогают: кадр говорит о блокировке сам.
//
// Тускнеет она не разом, а за доли секунды. Мгновенная подмена прозрачности выглядит как сбой
// отрисовки, а короткое затухание читается как ответ кнопки на действие игрока.
//
// Кнопок здесь две. SpriteButton принимает нажатие по всему кадру, RoundButton — по кругу
// внутри него. Круглыми нарисованы кнопки поля, прямоугольными — стрелки листалки попапа.

import { Circle, Sprite, type Texture } from 'pixi.js';

import { StateMachine, type Transitions } from '../game/stateMachine';
import { sfx } from '../sound/sound';

/**
 * Вид кнопки.
 *
 * idle — курсора над кнопкой нет.
 * hover — курсор над кнопкой.
 * active — кнопку нажали и ещё не отпустили.
 */
export type ButtonState = 'idle' | 'hover' | 'active';

/**
 * Переходы вида. Разрешены все, кроме перехода в себя же.
 *
 * Нажать можно и из idle: на тачскрине курсор не наводят, там сразу приходит нажатие.
 * Отпустить можно и в hover, и в idle: отпускают как над кнопкой, так и мимо неё.
 */
const TRANSITIONS: Transitions<ButtonState> = {
  idle: ['hover', 'active'],
  hover: ['idle', 'active'],
  active: ['idle', 'hover'],
};

/** Кадры кнопки: по кадру на каждый её вид. */
export type ButtonFaces = Readonly<Record<ButtonState, Texture>> & {
  /** Кадр заблокированной кнопки. Нет кадра — блокировка показывается прозрачностью. */
  readonly blocked?: Texture;
};

/**
 * Насколько видна заблокированная кнопка.
 *
 * Блокировка в игре одна: игра ждёт ответ бэкенда, и торопить ей нечего. Ждать приходится
 * игроку, а не машине, поэтому кнопка приглушается, но остаётся хорошо видной. Совсем тусклая
 * кнопка на долгом ожидании читалась бы как поломка.
 */
export const BLOCKED_ALPHA: number = 0.5;

/** За сколько кнопка гаснет и загорается обратно. */
export const FADE_SECONDS: number = 0.15;

/** Ведёт прозрачность к цели заданным шагом. Цель ближе шага — встаём ровно на неё. */
function fadeTo(current: number, target: number, step: number): number {
  if (current < target)
    return Math.min(target, current + step);

  return Math.max(target, current - step);
}

/** Кнопка, которая принимает нажатие по всему своему кадру. */
export class SpriteButton {
  /** Узел кнопки. Его начало координат стоит в середине кадра. */
  readonly view: Sprite;

  readonly #state = new StateMachine<ButtonState>('idle', TRANSITIONS);
  readonly #onClick: () => void;

  #faces: ButtonFaces;
  #enabled = true;

  /**
   * @param onClick Вызывается, когда и нажатие, и отпускание пришлись на разблокированную кнопку.
   */
  constructor(faces: ButtonFaces, onClick: () => void) {
    this.#faces = faces;
    this.#onClick = onClick;

    this.view = new Sprite(faces.idle);
    this.view.anchor.set(0.5);
    this.view.eventMode = 'static';
    this.view.cursor = 'pointer';

    this.#state.onChange(() => this.#refresh());

    this.view.on('pointerover', () => this.#show('hover'));
    this.view.on('pointerout', () => this.#show('idle'));
    this.view.on('pointerdown', () => this.#show('active'));
    this.view.on('pointerup', () => this.#show('hover'));
    this.view.on('pointerupoutside', () => this.#show('idle'));

    // pointertap приходит, только если нажатие и отпускание пришлись на кнопку. По pointerup
    // клик считать нельзя: он придёт и когда игрок нажал мимо, а отпустил над кнопкой.
    this.view.on('pointertap', () => this.press());
  }

  /**
   * Жмёт кнопку, как это сделала бы мышь.
   *
   * Заблокированная кнопка нажатие не принимает, откуда бы оно ни пришло. Звук идёт
   * на сработавшее нажатие: щелчок в ответ на ничто читался бы как приглашение жать ещё.
   */
  press(): void {
    if (!this.#enabled)
      return;

    sfx('button');
    this.#onClick();
  }

  /** Отвечает ли кнопка на нажатия. */
  get enabled(): boolean {
    return this.#enabled;
  }

  /**
   * Блокирует и разблокирует кнопку.
   *
   * Заблокированная кнопка тускнеет и держится спокойного кадра, но за курсором следить
   * не перестаёт. Поэтому в тот же миг, когда её разблокировали, она уже знает, лежит ли
   * под курсором.
   */
  set enabled(value: boolean) {
    this.#enabled = value;
    this.view.cursor = value ? 'pointer' : 'default';
    this.#refresh();
  }

  /**
   * Меняет кадры кнопки.
   *
   * Новый вид встаёт сразу в то состояние, в котором кнопка сейчас: наведённая кнопка
   * останется наведённой, нажатая — нажатой. Кадры обязаны быть того же размера,
   * что и при создании: по ним уже посчитана область нажатия.
   */
  set faces(value: ButtonFaces) {
    this.#faces = value;
    this.#refresh();
  }

  /** Ведёт прозрачность к той, что положена нынешнему виду кнопки. */
  update(seconds: number): void {
    // Свой кадр блокировки говорит о ней сам, и гасить такую кнопку нечем: она и так
    // отличается от живой.
    const dims: boolean = !this.#enabled && this.#faces.blocked === undefined;
    const target: number = dims ? BLOCKED_ALPHA : 1;
    const step: number = ((1 - BLOCKED_ALPHA) / FADE_SECONDS) * seconds;

    this.view.alpha = fadeTo(this.view.alpha, target, step);
  }

  /** Меняет вид кнопки. Тот же вид — не переход, слушателей он не касается. */
  #show(state: ButtonState): void {
    if (this.#state.state !== state)
      this.#state.goTo(state);
  }

  #refresh(): void {
    if (this.#enabled) {
      this.view.texture = this.#faces[this.#state.state];
      return;
    }

    this.view.texture = this.#faces.blocked ?? this.#faces.idle;
  }
}

/**
 * Круглая кнопка: нажатие она принимает только внутри круга.
 *
 * Круг занимает кадр целиком, поэтому его радиус — половина кадра, а середина совпадает
 * с началом координат спрайта: якорь стоит в середине кадра.
 */
export class RoundButton extends SpriteButton {
  /** Радиус круга. По нему кнопку отставляют от края экрана. */
  readonly radius: number;

  constructor(faces: ButtonFaces, onClick: () => void) {
    super(faces, onClick);

    this.radius = this.view.texture.width / 2;
    this.view.hitArea = new Circle(0, 0, this.radius);
  }
}
